import { Box, Button, Flex, TagLabel, Text, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ToggleButton } from "src/components/Buttons/ToggleButton";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { Glossary } from "src/components/Text/Glossary";
import {
  FILTER_NEGATION_PREFIX,
  FilterTypes,
  SearchAndFilterTypes,
} from "src/constants";
import { FilterSettings } from "./FilterSettings";
import { verifyFilterInclusion } from "./utils";

export const isNegated = (opt: string) =>
  opt.startsWith(FILTER_NEGATION_PREFIX);

export const negation = (opt: string) =>
  opt.startsWith(FILTER_NEGATION_PREFIX)
    ? opt.slice(FILTER_NEGATION_PREFIX.length)
    : `${FILTER_NEGATION_PREFIX}${opt}`;

export const parseNegatableFilterSetting = (options: string[]) =>
  Object.fromEntries(
    options.map((opt) =>
      isNegated(opt) ? [negation(opt), false] : [opt, true]
    )
  );

export type CommonFilterProps = {
  filterSettings: FilterSettings<SearchAndFilterTypes>;
};

export type Option = {
  label: string;
  id: string;
};
type OptionFilterProps = CommonFilterProps & {
  filterKey: SearchAndFilterTypes;
  options: Option[];
  onChange?: (values: string[]) => void;
};

type ButtonFilterProps = OptionFilterProps;
export const ButtonFilter = ({
  filterSettings,
  filterKey,
  options,
  onChange,
}: ButtonFilterProps) => {
  // Technically, this component doesn't need its own state, but since individual
  // changes to the filter settings don't trigger a re-render, we keep track of
  // the selected items here to ensure responsiveness of the buttons.
  const [selectedItems, setSelectedItems] = useState(
    filterSettings.get(filterKey)
  );
  useEffect(() => {
    setSelectedItems(filterSettings.get(filterKey));
  }, [filterSettings, filterKey]);

  return (
    <Flex flexWrap="wrap" gap={2}>
      {options.map((opt) => (
        <ToggleButton
          key={opt.id}
          active={selectedItems.includes(opt.id)}
          onClick={() => {
            const newSelection = selectedItems.filter((id) => id !== opt.id);
            if (newSelection.length === selectedItems.length)
              newSelection.push(opt.id);
            setSelectedItems(newSelection);
            filterSettings.toggle(filterKey, opt.id);
            if (onChange) onChange(newSelection);
          }}
          display="block"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {opt.label}
        </ToggleButton>
      ))}
    </Flex>
  );
};

type MultiSelectFilterProps = OptionFilterProps & {
  placeholder?: string;
  negatable?: boolean;
};
export const MultiSelectFilter = ({
  filterSettings,
  filterKey,
  options,
  placeholder,
  negatable = false,
}: MultiSelectFilterProps) => {
  const [selectedItems, setSelectedItems] = useState(() =>
    parseNegatableFilterSetting(filterSettings.get(filterKey))
  );
  useEffect(() => {
    setSelectedItems(
      parseNegatableFilterSetting(filterSettings.get(filterKey))
    );
  }, [filterSettings, filterKey]);

  return (
    <Box width="100%">
      <MultiSelectInput
        size="md"
        initialValues={options.filter((opt) => opt.id in selectedItems)}
        options={options}
        renderTagContent={(opt) => (
          <>
            {negatable && (
              <Button
                variant="solid"
                colorScheme="gray"
                backgroundColor="blackAlpha.300"
                size="xs"
                padding={1}
                margin={-1}
                marginRight={1}
                onClick={() => {
                  setSelectedItems({
                    ...selectedItems,
                    [opt.id]: !selectedItems[opt.id],
                  });
                  filterSettings.toggle(filterKey, opt.id, negation(opt.id));
                }}
              >
                {selectedItems[opt.id] ? "Has" : "Doesn't have"}
              </Button>
            )}
            <TagLabel>{opt.label}</TagLabel>
          </>
        )}
        placeholder={placeholder}
        onAddTag={(opt) => {
          filterSettings.add(filterKey, opt.id);
          setSelectedItems({ ...selectedItems, [opt.id]: true });
        }}
        onRemoveTag={(opt) => {
          filterSettings.remove(filterKey, opt.id, negation(opt.id));
          const { [opt.id]: _, ...newSelectedItems } = selectedItems;
          setSelectedItems(newSelectedItems);
        }}
      />
    </Box>
  );
};

type DynamicSelectFilterProps =
  | ({ optionStyle: "button" } & ButtonFilterProps)
  | ({ optionStyle: "multiSelect" } & MultiSelectFilterProps);
export const DynamicSelectFilter = ({
  optionStyle,
  ...props
}: DynamicSelectFilterProps) => {
  return optionStyle === "button" ? (
    <ButtonFilter {...props} />
  ) : (
    <MultiSelectFilter {...props} />
  );
};

type FormFilterProps = React.PropsWithChildren<{
  label: string;
}>;
export const FormFilter = ({ label, children }: FormFilterProps) => {
  return (
    <VStack align="inherit">
      <Text variant="filterLabel">
        <Glossary>{label}</Glossary>
      </Text>
      {children}
    </VStack>
  );
};

type FormFilterControllerProps = React.PropsWithChildren<{
  filter: FilterTypes;
}>;
export const FormFilterController = ({
  children,
  filter,
}: FormFilterControllerProps) => {
  const [searchParams] = useSearchParams();
  return verifyFilterInclusion(searchParams, filter) ? children : null;
};
